import {
  EventBus,
  EventHandler,
  EventType,
  SymbolTokenEventType,
  Serializable,
} from '@servicetitan/web-components';
import IWebPhoneService from './IWebPhoneService';
import handleWebPhoneDialerStateUpdate from './events/handleWebPhoneDialerStateUpdate';
import {
  AudioDevicesChangedPayload,
  ConnectionStatusUpdatePayload,
  DeviceInitFailedPayload,
  DeviceListChangedPayload,
  DevicesReadyPayload,
  MicMonitorFailedPayload,
  MicMonitorStartedPayload,
  DialerState,
  OutboundCallerIdInput,
  SetAudioDevicesFailedPayload,
  NetworkTestResultPayload,
  StartNetworkTestArgs,
  WebphoneCallEventPayload,
  WebphonePhoneStateEvent,
} from './types';
import IAppLogger from '@app/lib/IAppLogger';
import handleRegistrationOverwritten from './events/handleRegistrationOverwritten';
import handleConnectionStatusUpdate from './events/handleConnectionStatusUpdate';
import handleCallFailed from './events/handleCallFailed';
import handlePhoneStateUpdate from './events/handlePhoneStateUpdate';
import AuthModule from '@modules/AuthModule';
import { handleDebugInfo } from './events/handleDebugInfo';
import {
  outgoingEventNames,
  phoneCommandNames,
  webPhoneEventNames,
  webPhoneLastTriggeringEventNames,
} from './constants/events';
import handleWebphoneCallEnded from './events/handleWebphoneCallEnded';
import handleDevicesReady from './events/handleDevicesReady';
import handleDeviceListChanged from './events/handleDeviceListChanged';
import handleDeviceInitializationFailed from './events/handleDeviceInitializationFailed';
import handleAudioDevicesChanged from './events/handleAudioDevicesChanged';
import handleSetAudioDevicesFailed from './events/handleSetAudioDevicesFailed';
import handleMicMonitorStarted from './events/handleMicMonitorStarted';
import handleMicMonitorStopped from './events/handleMicMonitorStopped';
import handleMicMonitorFailed from './events/handleMicMonitorFailed';
import handleNetworkTestProgress from './events/handleNetworkTestProgress';
import handleNetworkTestResult from './events/handleNetworkTestResult';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
  OperationNames,
  CallDirection,
  CallConnectionErrorTypes,
} from '@modules/DiagnosticsModule';
import {
  clearAnswerRequested,
  markAnswerRequested,
} from './lib/callAcceptLatency';

/**
 * Handles the communication with Telecom's WebPhone.
 */
class WebPhoneService implements IWebPhoneService {
  private busListenerCleanupFunctions: (() => void)[] = [];

  private eventBus: EventBus | null = null;
  private logger: IAppLogger | null = null;

  /**
   * Answers an incoming call.
   */
  public answer(callId?: string): void {
    if (callId) {
      markAnswerRequested(callId);
    }
    this.emitCommand(phoneCommandNames.answer, { callId });
  }

  /**
   * Rejects a call.
   */
  public cancel(callId?: string): void {
    if (callId) {
      clearAnswerRequested(callId);
    }
    this.emitCommand(phoneCommandNames.cancel, { callId });
  }

  /**
   * Hangs up the current call.
   */
  public hangup(callId?: string): void {
    this.emitCommand(phoneCommandNames.hangup, { callId });
  }

  /**
   * Puts the current call on hold.
   */
  public hold(): void {
    this.emitCommand(phoneCommandNames.hold, {});
  }

  /**
   * Resumes a held call.
   */
  public unhold(callId?: string): void {
    this.emitCommand('unhold', { callId });
  }

  /**
   * Mutes the microphone during a call.
   */
  public mute(): void {
    this.emitCommand(phoneCommandNames.mute, {});
  }

  /**
   * Unmutes the microphone during a call.
   */
  public unmute(): void {
    this.emitCommand(phoneCommandNames.unmute, {});
  }

  /**
   * Enters a phone number to dial pad
   * @param number - The phone number to dial.
   */
  public dial(number: string): void {
    this.emitCommand(phoneCommandNames.dial, { number });
  }

  /**
   * Clears the entered digits in the dial pad
   */
  public clear(): void {
    this.emitCommand(phoneCommandNames.clear, {});
  }

  /**
   * Deletes the last entered digit in the dial pad.
   */
  public backspace(): void {
    this.emitCommand(phoneCommandNames.backspace, {});
  }

  /**
   * Performs an automatic action on the phone.
   */
  public autoAction(): void {
    this.emitCommand(phoneCommandNames.autoAction, {});
  }

  public callNumber(number: string) {
    this.emitCommand(phoneCommandNames.callNumber, { number });
  }

  /**
   * Calls a phone number.
   * @param number - The phone number to call.
   */
  /**
   * Initiates a call to the specified phone number and returns a promise that resolves
   * when the call is successfully started or rejects if the call fails.
   *
   * @param number - The phone number to call.
   * @param callerId - Optional caller ID information to use for the outbound call.
   * @returns A promise that resolves when the call is successfully started or rejects
   *          with an error if the call fails.
   *
   * @throws Error - If the call fails, the promise is rejected with an error containing
   *                 the failure reason.
   *
   * @remarks
   * This method listens for specific events (`phoneState` and `callEvent`) to determine
   * the success or failure of the call. Event listeners are cleaned up after the call
   * is completed or fails.
   *
   * A call is deemed successful if the `phoneState` event indicates that the call has
   * started before a call failed event. A call is deemed failed if the call failure event
   * is emitted before the call started event.
   *
   */
  public callNumberWithCallId(
    number: string,
    callerId: OutboundCallerIdInput,
  ): Promise<void> {
    const CALL_NUMBER_TIMEOUT = 30_000;

    const promise = new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
          [MetricTags.OPERATION_NAME]: OperationNames.CALL_CONNECTION,
          [MetricTags.DIRECTION]: CallDirection.Outbound.toLowerCase(),
          [MetricTags.ERROR_TYPE]: CallConnectionErrorTypes.TIMEOUT,
        });
        cleanUpListeners();
        reject(new Error('Call timed out after 30 seconds'));
      }, CALL_NUMBER_TIMEOUT);

      const outboundSuccessHandler = (event: WebphonePhoneStateEvent) => {
        // successful if call started with the given number
        if (
          event.triggeringEvent ===
            webPhoneLastTriggeringEventNames.callStarted &&
          event.callState.remoteIdentity === number
        ) {
          cleanUpListeners();
          clearTimeout(timeout);
          resolve();
        }
      };

      const cleanUpListeners = () => {
        this.eventBus?.off(
          webPhoneEventNames.phoneState,
          outboundSuccessHandler,
        );
      };

      this.addBusListener(
        webPhoneEventNames.phoneState,
        outboundSuccessHandler,
      );
    });
    this.emitCommand(phoneCommandNames.callNumber, { number, callerId });
    return promise;
  }

  public disconnect(): void {
    this.emitCommand(phoneCommandNames.disconnect, {});
  }

  public transfer(number: string): void {
    this.emitCommand(phoneCommandNames.transfer, { number });
  }

  public warmTransfer(targetCallId: string): void {
    this.emitCommand(phoneCommandNames.warmTransfer, { targetCallId });
  }

  /**
   * Initializes the WebPhoneService with the specified event bus.
   * @param eventBus - The event bus to use for communication.
   */
  public initialize(eventBus: EventBus, logger: IAppLogger): void {
    this.eventBus = eventBus;
    this.logger = logger;
    this.registerEventHandlers();
  }

  /**
   * Uninitializes the WebPhoneService and cleans up the event bus listeners.
   * Good for cleaning up {@link initialize} when the MFE (ergo the event bus) is unmounted
   */
  public uninitialize(): void {
    this.cleanUpBusListeners();
    this.eventBus = null;
    this.logger = null;
  }

  public overrideRegistration() {
    this.emitCommand(phoneCommandNames.connect, {
      overwriteExistingRegistrations: true,
    });
  }

  public collectDiagnostics() {
    this.emitCommand(phoneCommandNames.collectDebugInfo);
  }

  public reportConnectionInfo() {
    this.emitCommand(phoneCommandNames.reportConnectionInfo);
  }

  public startMicMonitor(): void {
    this.emitCommand(phoneCommandNames.startMicMonitor);
  }

  public stopMicMonitor(): void {
    this.emitCommand(phoneCommandNames.stopMicMonitor);
  }

  /**
   * Requests the webphone MFE to retry audio device initialization.
   * Should be called after a device-initialization-failed event with errorCategory 'device_not_found'.
   */
  public retryDeviceInitialization(): void {
    this.emitCommand(phoneCommandNames.retryDeviceInitialization);
  }

  /**
   * Starts the network test flow (host command); webphone MFE shows the test UI.
   */
  public startNetworkTest(args: StartNetworkTestArgs): void {
    this.emitCommand(phoneCommandNames.startNetworkTest, args);
  }

  /**
   * Requests the webphone MFE to switch active audio input, output, and ring devices.
   */
  public setAudioDevices(
    audioInput: string,
    audioOutput: string,
    ringOutput: string,
  ): void {
    this.emitCommand(phoneCommandNames.setAudioDevices, {
      audioInputDeviceId: audioInput,
      audioOutputDeviceId: audioOutput,
      ringOutputDeviceId: ringOutput,
    });
  }

  public reconnect(newWebphoneJwt: string) {
    this.emitCommand(phoneCommandNames.connect, {
      overwriteExistingRegistrations: true,
      newWebphoneJwt,
    });
  }

  private registerEventHandlers() {
    // dialerState update
    this.addBusListener(
      webPhoneEventNames.dialerState,
      (dialerState: DialerState) => {
        this.logger?.debug('Dialer state updated', dialerState);
        handleWebPhoneDialerStateUpdate(this, dialerState);
      },
    );

    // phoneState update
    this.addBusListener<WebphonePhoneStateEvent>(
      webPhoneEventNames.phoneState,
      (phoneState) => {
        this.logger?.debug('Phone state updated', phoneState);
        handlePhoneStateUpdate(this, phoneState, this.logger!);
      },
    );

    // registration overwritten
    this.addBusListener(webPhoneEventNames.registrationOverwritten, () => {
      this.logger?.debug('Registration Overwritten');
      handleRegistrationOverwritten();
    });

    // call-failed event
    this.addBusListener<WebphoneCallEventPayload>(
      webPhoneEventNames.callEvent,
      (event) => {
        if (event.eventType === 'webphone:call-failed') {
          this.logger?.debug('Call failed event', event);
          handleCallFailed(
            this,
            { ...event.event, eventType: event.eventType },
            this.logger!,
          );
        }
        if (event.eventType === 'webphone:call-ended') {
          this.logger?.debug('Call ended event', event);
          handleWebphoneCallEnded(
            { ...event.event, eventType: event.eventType },
            this.logger!,
          );
        }
      },
    );

    // pcs token expired
    this.addBusListener(webPhoneEventNames.pcsTokenExpired, () => {
      this.logger?.debug('PCS token expired');
      this.emitPcsUserToken();
    });

    // webphone diagnostics
    this.addBusListener(webPhoneEventNames.debugInfo, (debugInfo: any) => {
      this.logger?.debug('debug info received', debugInfo);
      handleDebugInfo(debugInfo);
    });

    // connection status update
    this.addBusListener<ConnectionStatusUpdatePayload>(
      webPhoneEventNames.connectionStatusUpdate,
      (event) => {
        this.logger?.debug('Connection status update', event);
        handleConnectionStatusUpdate(event, this.logger!);
      },
    );

    // DevicesReadyPayload includes microphoneAnalyser (AnalyserNode) which is not
    // JSON-serializable. Overload B of addBusListener handles non-Serializable payloads.
    this.addBusListener<DevicesReadyPayload>(
      webPhoneEventNames.devicesReady,
      (event) => handleDevicesReady(event, this.logger!),
    );

    // device list changed (hardware plug/unplug)
    this.addBusListener<DeviceListChangedPayload>(
      webPhoneEventNames.deviceListChanged,
      (event) => handleDeviceListChanged(event, this.logger!),
    );

    // device initialization failed
    this.addBusListener<DeviceInitFailedPayload>(
      webPhoneEventNames.deviceInitializationFailed,
      (event) => handleDeviceInitializationFailed(event, this.logger!),
    );

    // audio devices changed (successful switch)
    this.addBusListener<AudioDevicesChangedPayload>(
      webPhoneEventNames.audioDevicesChanged,
      (event) => handleAudioDevicesChanged(event, this.logger!),
    );

    // set audio devices failed
    this.addBusListener<SetAudioDevicesFailedPayload>(
      webPhoneEventNames.setAudioDevicesFailed,
      (event) => handleSetAudioDevicesFailed(event, this.logger!),
    );

    this.addBusListener<MicMonitorStartedPayload>(
      webPhoneEventNames.micMonitorStarted,
      (event) => handleMicMonitorStarted(event, this.logger!),
    );

    this.addBusListener(webPhoneEventNames.micMonitorStopped, () =>
      handleMicMonitorStopped(this.logger!),
    );

    this.addBusListener<MicMonitorFailedPayload>(
      webPhoneEventNames.micMonitorFailed,
      (event) => handleMicMonitorFailed(event, this.logger!),
    );

    this.addBusListener(webPhoneEventNames.networkTestProgress, () => {
      this.logger?.debug('Network test progress');
      handleNetworkTestProgress();
    });

    this.addBusListener<NetworkTestResultPayload>(
      webPhoneEventNames.networkTestResult,
      (event) => {
        this.logger?.debug('Network test result', event);
        handleNetworkTestResult(event);
      },
    );
  }

  /**
   * Removes all of the listeners added to the current event bus
   */
  private cleanUpBusListeners() {
    this.busListenerCleanupFunctions.forEach((cleanupFunction) =>
      cleanupFunction(),
    );
    this.busListenerCleanupFunctions = [];
  }

  private emitCommand(command: string, args?: any): void {
    this.eventBus?.emit(outgoingEventNames.hostCommand, {
      command: command,
      args: args,
    });
  }

  private async emitPcsUserToken(): Promise<void> {
    const jwt = await AuthModule.getCurrentToken();
    this.eventBus?.emit(outgoingEventNames.newPcsToken, {
      jwt,
    });
  }

  /**
   * Overload A: use for JSON-serializable payloads.
   * Enforces the Serializable<T> constraint so the payload type is verified at compile time.
   */
  private addBusListener<
    T extends Serializable<T>,
    U extends EventType = EventType,
  >(type: SymbolTokenEventType<T, U>, handler: EventHandler<never, U, T>): void;
  /**
   * Overload B: use for payloads that include non-serializable Web API objects (e.g. AnalyserNode).
   * Bypasses the Serializable constraint — the event bus passes the real payload at runtime regardless.
   */
  private addBusListener<T, U extends EventType = EventType>(
    type: SymbolTokenEventType<T, U>,
    handler: (payload: T) => void,
  ): void;
  /**
   * Adds a listener to the current event bus.
   * Should be used instead of eventBus.on to ensure that the listeners are cleaned up when the WebPhoneService is uninitialized
   * @param type - The type of event to listen for
   * @param handler - The handler to call when the event is emitted
   */
  private addBusListener<T, U extends EventType = EventType>(
    type: SymbolTokenEventType<T, U>,
    handler: EventHandler<never, U, T> | ((payload: T) => void),
  ): void {
    const busType = type as Parameters<EventBus['on']>[0];
    const busHandler = handler as Parameters<EventBus['on']>[1];
    this.busListenerCleanupFunctions.push(() => {
      this.eventBus?.off(busType, busHandler);
    });
    this.eventBus?.on(busType, busHandler);
  }
}

export default new WebPhoneService();
